import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined'
import { Button } from 'antd'
import { FfsAnalysisResult } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsResultScenarios'
import { useFfsStrategy } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/FfsStrategyContext'
import { FinalAcceptabilityCriteriaCheckResult } from 'features/indicationTriage/cards/ffsCalc/part4/FfsFinalAcceptabilityCriteriaSection'
import { FlawSizeCriteriaCheckResult } from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsFlawSizeCriteriaSection'
import React, { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement } from 'types'

import { RESULT_OUTCOME_COLORS } from './ffsLtaPublishApi'
import { FfsResultCriteriaModal } from './FfsResultCriteriaModal'
import { FfsResultType } from './ffsTypes'

const ResultsContainer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 0.5rem;
`

interface ResultHeaderProps {
  $resultStatus: FfsResultType
}

const ResultHeader = styled.div<ResultHeaderProps>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  color: ${({ $resultStatus }) => RESULT_OUTCOME_COLORS[$resultStatus]};
`

interface ResultBadgeProps {
  $analysisResult: FfsResultType
}

const ResultBadge = styled.span<ResultBadgeProps>`
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${({ $analysisResult }) =>
    RESULT_OUTCOME_COLORS[$analysisResult]};
  color: white;
`

const ResultHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`

const InfoButton = styled(Button)`
  border: none;
  box-shadow: none;
  padding: 0;
  height: auto;
  min-width: auto;

  &:hover {
    background: transparent;
  }

  .anticon {
    color: ${({ theme }) => theme.colors.grayscale[500]};
    font-size: 0.875rem;

    &:hover {
      color: ${({ theme }) => theme.colors.SkyBlue};
    }
  }
`

const ConditionText = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.4;
`

const ExplanationText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
`

const FailedCriteriaList = styled.ul`
  margin: 0.5rem 0 0 0;
  padding-left: 1.25rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.error[600]};
`

export interface FfsPartFourResultsProps {
  finalAcceptabilityCriteriaCheckProps: FinalAcceptabilityCriteriaCheckResult
}

export interface FfsPartFiveResultsProps {
  rsf: GeckoMeasurement | null
  flawSizeCheckResult: FlawSizeCriteriaCheckResult
}
export interface FfsAnalysisResultsSectionProps {
  mawpr: GeckoMeasurement | null
  mawpDesign: GeckoMeasurement | null
  resultProps: FfsPartFourResultsProps | FfsPartFiveResultsProps
}

export const useAnalysisResult = ({
  mawpr,
  mawpDesign,
  resultProps,
}: FfsAnalysisResultsSectionProps): FfsAnalysisResult => {
  const {
    strategy: { analysisResult: strategyAnalysisResult },
  } = useFfsStrategy()

  return useMemo(() => {
    const partFourResultProps = resultProps as FfsPartFourResultsProps
    const partFiveResultProps = resultProps as FfsPartFiveResultsProps

    return strategyAnalysisResult.computeAnalysisResult({
      mawpr,
      mawpDesign,
      finalAcceptabilityCriteriaCheckProps:
        partFourResultProps.finalAcceptabilityCriteriaCheckProps,
      rsf: partFiveResultProps.rsf,
      flawSizeCheckResult: partFiveResultProps.flawSizeCheckResult,
    })
  }, [mawpr, mawpDesign, resultProps, strategyAnalysisResult])
}

export const FfsAnalysisResultsSection: FC<{
  analysisResult: FfsAnalysisResult
}> = ({ analysisResult }) => {
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <ResultsContainer>
      <ResultHeader $resultStatus={analysisResult.resultType}>
        <ResultHeaderContent>
          <ResultBadge $analysisResult={analysisResult.resultType}>
            {analysisResult.resultLabel}
          </ResultBadge>
          Analysis Result
        </ResultHeaderContent>
        <InfoButton
          type='text'
          icon={<InfoCircleOutlined />}
          onClick={() => setModalOpen(true)}
          title='View analysis criteria'
        />
      </ResultHeader>

      <ConditionText>{analysisResult.condition}</ConditionText>
      <ExplanationText>{analysisResult.explanation}</ExplanationText>
      {analysisResult.failedCriteria &&
        analysisResult.failedCriteria.length > 0 && (
          <FailedCriteriaList>
            {analysisResult.failedCriteria.map(
              (criteria: string, index: number) => (
                <li key={index}>{criteria}</li>
              )
            )}
          </FailedCriteriaList>
        )}

      <FfsResultCriteriaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </ResultsContainer>
  )
}
